/**
 * MobileLanguagePicker — compact globe icon + bottom-sheet picker for
 * the Food Pulse mobile web app. Sets the `googtrans` cookie so Google
 * Translate flips the whole app to the chosen language (same mechanism
 * used by the desktop <TranslateWidget />).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Globe, X, Check } from "lucide-react";

const LANGS = [
  { code: "en",    label: "English"           },
  { code: "es",    label: "Español"           },
  { code: "hi",    label: "हिन्दी"            },
  { code: "ta",    label: "தமிழ்"             },
  { code: "ml",    label: "മലയാളം"           },
  { code: "te",    label: "తెలుగు"            },
  { code: "kn",    label: "ಕನ್ನಡ"             },
  { code: "mr",    label: "मराठी"             },
  { code: "bn",    label: "বাংলা"             },
  { code: "gu",    label: "ગુજરાતી"           },
  { code: "pa",    label: "ਪੰਜਾਬੀ"            },
  { code: "ur",    label: "اردو"              },
  { code: "ar",    label: "العربية"           },
  { code: "fr",    label: "Français"          },
  { code: "de",    label: "Deutsch"           },
  { code: "pt",    label: "Português"         },
  { code: "ru",    label: "Русский"           },
  { code: "zh-CN", label: "中文 (简体)"        },
  { code: "ja",    label: "日本語"            },
  { code: "ko",    label: "한국어"            },
  { code: "it",    label: "Italiano"          },
  { code: "nl",    label: "Nederlands"        },
  { code: "tr",    label: "Türkçe"            },
  { code: "vi",    label: "Tiếng Việt"        },
  { code: "th",    label: "ภาษาไทย"          },
  { code: "id",    label: "Bahasa Indonesia"  },
];

const GT_SCRIPT_ID = "google-translate-script";
const GT_ROOT_ID   = "gt_widget_root";
const COOKIE_NAME  = "googtrans";

function readActive() {
  const raw = (typeof document !== "undefined" ? document.cookie : "") || "";
  const m = raw.match(/(?:^|; )googtrans=([^;]+)/);
  if (!m) return "en";
  const v = decodeURIComponent(m[1]).split("/").filter(Boolean);
  return v[v.length - 1] || "en";
}

function loadGoogleScript() {
  if (typeof window === "undefined") return;
  if (document.getElementById(GT_SCRIPT_ID)) return;
  if (!document.getElementById(GT_ROOT_ID)) {
    const host = document.createElement("div");
    host.id = GT_ROOT_ID;
    host.style.cssText = "position:absolute;left:-9999px;top:-9999px;height:0;width:0;overflow:hidden;";
    document.body.appendChild(host);
  }
  window.googleTranslateElementInit = () => {
    // eslint-disable-next-line no-new
    new window.google.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false, layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE },
      GT_ROOT_ID
    );
  };
  const s = document.createElement("script");
  s.id = GT_SCRIPT_ID;
  s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.body.appendChild(s);
}

function applyLang(code) {
  if (typeof document === "undefined") return;
  // Google recognises cookie of the form /source/target set on host + parent domain.
  const value = `/en/${code}`;
  const host = window.location.hostname;
  const parent = host.split(".").slice(-2).join(".");
  document.cookie = `${COOKIE_NAME}=${value}; path=/`;
  document.cookie = `${COOKIE_NAME}=${value}; path=/; domain=.${parent}`;
  // Also on the exact host (some browsers require it).
  document.cookie = `${COOKIE_NAME}=${value}; path=/; domain=${host}`;
  // Reload so Google Translate picks up the new cookie and translates
  // the freshly-rendered DOM. English resets removes the cookie.
  if (code === "en") {
    document.cookie = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${COOKIE_NAME}=; path=/; domain=.${parent}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${COOKIE_NAME}=; path=/; domain=${host}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
  window.location.reload();
}

export function MobileLanguagePickerButton() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const active = useMemo(() => readActive(), [open]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeLabel = useMemo(() => (LANGS.find((l) => l.code === active) || LANGS[0]).label, [active]);
  const sheetRef = useRef(null);

  useEffect(() => { loadGoogleScript(); }, []);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const filtered = q.trim()
    ? LANGS.filter((l) => l.label.toLowerCase().includes(q.trim().toLowerCase()) || l.code.includes(q.trim().toLowerCase()))
    : LANGS;

  return (
    <>
      <button
        aria-label={`Language — currently ${activeLabel}`}
        onClick={() => setOpen(true)}
        data-testid="mobile-language-btn"
        className="w-9 h-9 rounded-full hover:bg-white/20 flex items-center justify-center"
        title={`Language: ${activeLabel}`}
      >
        <Globe className="w-5 h-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end"
          data-testid="mobile-language-sheet"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            ref={sheetRef}
            className="relative w-full max-h-[85vh] bg-white rounded-t-3xl overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white px-5 pt-4 pb-3 border-b z-10 flex items-center justify-between">
              <h2 className="text-lg font-bold">Choose your language</h2>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="mobile-language-close"
                className="w-8 h-8 rounded-full border flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 pt-3">
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search language"
                data-testid="mobile-language-search"
                data-no-translate="true"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 outline-none text-base bg-white"
              />
            </div>
            <ul className="pb-6 mt-2">
              {filtered.map((l) => {
                const sel = l.code === active;
                return (
                  <li key={l.code}>
                    <button
                      onClick={() => applyLang(l.code)}
                      data-testid={`mobile-language-opt-${l.code}`}
                      className="w-full px-5 py-4 text-left border-b flex items-center justify-between hover:bg-gray-50"
                      style={{ background: sel ? "#F9FAFB" : "transparent" }}
                    >
                      <span className="text-base">{l.label}</span>
                      {sel && <Check className="w-5 h-5" style={{ color: "#653091" }} />}
                    </button>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="px-5 py-6 text-sm text-gray-500">No languages match "{q}".</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
